import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight, HardDrive, X } from 'lucide-react';
import {
  apiClient,
  type AdminStorageUsageItem,
  type AdminStorageUsageQuery,
  type ApiError,
  type StorageQuotaSource,
} from '@/lib/api-client';
import { bytesToGb, formatBytes, gbToBytes } from '@/lib/format-bytes';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getErrorMessage(err: unknown): string {
  if (err instanceof Error && err.message) return err.message;
  const apiErr = err as ApiError | undefined;
  if (apiErr?.message) return apiErr.message;
  return 'Erro inesperado. Tente novamente.';
}

/**
 * 'env' e 'default' viram "Padrão" na UI — o dono nao precisa distinguir
 * "veio de env" de "veio do fallback hardcoded", so importa que nao ha
 * ajuste manual nem quota de plano.
 */
const QUOTA_SOURCE_LABEL: Record<StorageQuotaSource, string> = {
  override: 'Override',
  plan: 'Plano',
  env: 'Padrão',
  default: 'Padrão',
};

const QUOTA_SOURCE_BADGE_CLASS: Record<StorageQuotaSource, string> = {
  override: 'bg-violet-900/40 text-violet-200',
  plan: 'bg-sky-900/40 text-sky-200',
  env: 'bg-neutral-800 text-neutral-400',
  default: 'bg-neutral-800 text-neutral-400',
};

function usageBarColor(percent: number): string {
  if (percent > 95) return 'bg-red-500';
  if (percent > 80) return 'bg-amber-500';
  return 'bg-emerald-500';
}

function usagePercentTextColor(percent: number): string {
  if (percent > 95) return 'text-red-400';
  if (percent > 80) return 'text-amber-400';
  return 'text-neutral-300';
}

const PAGE_SIZE = 20;

type SortBy = NonNullable<AdminStorageUsageQuery['sortBy']>;
type SortDir = NonNullable<AdminStorageUsageQuery['sortDir']>;

interface SortableColumn {
  key: SortBy;
  label: string;
  align?: 'right';
}

const COLUMNS: SortableColumn[] = [
  { key: 'tenantName', label: 'Escritório' },
  { key: 'quotaBytes', label: 'Quota', align: 'right' },
  { key: 'usedBytes', label: 'Usado', align: 'right' },
  { key: 'usagePercent', label: '%', align: 'right' },
];

// ---------------------------------------------------------------------------
// Modal — ajustar espaço (override manual de quota por tenant)
// ---------------------------------------------------------------------------

interface AdjustSpaceModalProps {
  item: AdminStorageUsageItem;
  onClose: () => void;
  onSaved: () => void;
}

function AdjustSpaceModal({ item, onClose, onSaved }: AdjustSpaceModalProps) {
  const [gbValue, setGbValue] = useState<string>(
    item.quotaSource === 'override' ? String(bytesToGb(item.quotaBytes)) : '',
  );

  const setMutation = useMutation({
    mutationFn: (overrideBytes: number) =>
      apiClient.adminSetStorageOverride(item.tenantId, overrideBytes),
    onSuccess: onSaved,
  });

  const clearMutation = useMutation({
    mutationFn: () => apiClient.adminClearStorageOverride(item.tenantId),
    onSuccess: onSaved,
  });

  const isBusy = setMutation.isPending || clearMutation.isPending;

  function handleSave() {
    const parsed = Number(gbValue.replace(',', '.'));
    if (!Number.isFinite(parsed) || parsed < 0) return;
    setMutation.mutate(gbToBytes(parsed));
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm overflow-hidden rounded-lg border border-neutral-800 bg-neutral-900 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-neutral-800 px-5 py-4">
          <div>
            <h3 className="text-sm font-semibold text-neutral-100">Ajustar espaço</h3>
            <p className="mt-0.5 text-xs text-neutral-400">{item.tenantName}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-neutral-400 hover:bg-neutral-800 hover:text-neutral-100"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          <div className="rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-xs text-neutral-400">
            Quota atual:{' '}
            <span className="font-medium text-neutral-200">{formatBytes(item.quotaBytes)}</span>{' '}
            · fonte: {QUOTA_SOURCE_LABEL[item.quotaSource]}
          </div>

          <div className="space-y-1.5">
            <label htmlFor="adjust-space-gb" className="block text-sm font-medium text-neutral-300">
              Novo espaço (GB)
            </label>
            <input
              id="adjust-space-gb"
              type="number"
              step="0.1"
              min="0"
              value={gbValue}
              onChange={(e) => setGbValue(e.target.value)}
              placeholder="ex: 10"
              className="w-full rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 text-sm text-neutral-100 placeholder-neutral-500 focus:border-neutral-500 focus:outline-none focus:ring-1 focus:ring-neutral-500"
            />
          </div>

          <button
            type="button"
            onClick={() => clearMutation.mutate()}
            disabled={isBusy || item.quotaSource !== 'override'}
            className="text-xs text-neutral-400 underline underline-offset-2 hover:text-neutral-200 disabled:cursor-not-allowed disabled:opacity-40 disabled:no-underline"
          >
            Voltar a herdar do plano
          </button>

          {(setMutation.isError || clearMutation.isError) && (
            <p className="text-xs text-red-400">
              {getErrorMessage(setMutation.error ?? clearMutation.error)}
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-neutral-800 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-neutral-700 px-3 py-1.5 text-sm font-medium text-neutral-300 hover:bg-neutral-800"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isBusy || gbValue.trim() === ''}
            className="rounded-md bg-neutral-100 px-3 py-1.5 text-sm font-medium text-neutral-900 hover:bg-white disabled:opacity-50"
          >
            {setMutation.isPending ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Lista
// ---------------------------------------------------------------------------

function LoadingSkeleton() {
  return (
    <div className="overflow-hidden rounded-lg border border-neutral-800 bg-neutral-900">
      <div className="divide-y divide-neutral-800">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 px-4 py-3">
            <div className="h-4 w-40 animate-pulse rounded bg-neutral-800" />
            <div className="h-4 w-20 animate-pulse rounded bg-neutral-800" />
            <div className="ml-auto h-4 w-32 animate-pulse rounded bg-neutral-800" />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminArmazenamentoPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<SortBy>('usagePercent');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [adjustItem, setAdjustItem] = useState<AdminStorageUsageItem | null>(null);

  const { data, isPending, isError, error, isFetching } = useQuery({
    queryKey: ['admin', 'storage-usage', page, sortBy, sortDir],
    queryFn: () =>
      apiClient.adminStorageUsage({ page, pageSize: PAGE_SIZE, sortBy, sortDir }),
  });

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'storage-usage'] });
  }

  function handleSort(key: SortBy) {
    if (key === sortBy) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir(key === 'tenantName' ? 'asc' : 'desc');
    }
    setPage(1);
  }

  const items = data?.items ?? [];
  const totalPages = data?.totalPages ?? 1;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight text-neutral-100">
          Gerenciador de disco
        </h1>
        <p className="mt-1 text-sm text-neutral-400">
          Consumo de armazenamento por escritório, quota efetiva (override, plano ou padrão) e
          ajuste manual por tenant.
        </p>
      </header>

      {isPending ? (
        <LoadingSkeleton />
      ) : isError ? (
        <div className="rounded-lg border border-red-900/50 bg-red-950/30 p-4 text-sm text-red-200">
          Não foi possível carregar o consumo de armazenamento agora.
          {error instanceof Error && error.message ? (
            <span className="ml-1 opacity-70">({error.message})</span>
          ) : null}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-8 text-center">
          <HardDrive className="mx-auto mb-2 h-8 w-8 text-neutral-600" />
          <p className="text-sm text-neutral-400">Nenhum escritório encontrado.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-neutral-800 bg-neutral-900">
          <table className="w-full text-sm">
            <thead className="border-b border-neutral-800 bg-neutral-950/40 text-left text-xs font-medium uppercase tracking-wide text-neutral-400">
              <tr>
                {COLUMNS.map((col) => (
                  <th key={col.key} className={`px-4 py-3 ${col.align === 'right' ? 'text-right' : ''}`}>
                    <button
                      type="button"
                      onClick={() => handleSort(col.key)}
                      className={`inline-flex items-center gap-1 hover:text-neutral-200 ${
                        col.align === 'right' ? 'flex-row-reverse' : ''
                      }`}
                    >
                      {col.label}
                      {sortBy === col.key ? (
                        sortDir === 'asc' ? (
                          <ArrowUp className="h-3 w-3" />
                        ) : (
                          <ArrowDown className="h-3 w-3" />
                        )
                      ) : (
                        <ArrowUpDown className="h-3 w-3 opacity-40" />
                      )}
                    </button>
                  </th>
                ))}
                <th className="px-4 py-3">Plano</th>
                <th className="px-4 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-800">
              {items.map((item) => {
                const percent = Math.min(100, Math.max(0, item.usagePercent));
                return (
                  <tr key={item.tenantId} className="hover:bg-neutral-800/40">
                    <td className="px-4 py-3">
                      <div className="font-medium text-neutral-100">{item.tenantName}</div>
                      <div className="font-mono text-xs text-neutral-500">{item.tenantSlug}</div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-neutral-300">{formatBytes(item.quotaBytes)}</span>
                      <span
                        className={`ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${QUOTA_SOURCE_BADGE_CLASS[item.quotaSource]}`}
                      >
                        {QUOTA_SOURCE_LABEL[item.quotaSource]}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col items-end gap-1">
                        <span className="text-neutral-300">{formatBytes(item.usedBytes)}</span>
                        <div className="h-1.5 w-28 overflow-hidden rounded-full bg-neutral-800">
                          <div
                            className={`h-full rounded-full ${usageBarColor(item.usagePercent)}`}
                            style={{ width: `${percent}%` }}
                          />
                        </div>
                      </div>
                    </td>
                    <td className={`px-4 py-3 text-right font-medium ${usagePercentTextColor(item.usagePercent)}`}>
                      {item.usagePercent.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%
                    </td>
                    <td className="px-4 py-3 text-neutral-300">
                      {item.planName ?? <span className="text-neutral-600">Sem plano</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => setAdjustItem(item)}
                        className="rounded-md border border-neutral-700 px-2.5 py-1 text-xs font-medium text-neutral-300 hover:bg-neutral-800"
                      >
                        Ajustar espaço
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="flex items-center justify-between gap-3 border-t border-neutral-800 px-4 py-3">
            <span className="text-xs text-neutral-500">
              {data?.total ?? 0} escritório{(data?.total ?? 0) === 1 ? '' : 's'}
              {isFetching ? ' · atualizando...' : ''}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-neutral-700 text-neutral-300 hover:bg-neutral-800 disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="Página anterior"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </button>
              <span className="text-xs text-neutral-400">
                Página {data?.page ?? page} de {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-neutral-700 text-neutral-300 hover:bg-neutral-800 disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="Próxima página"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {adjustItem && (
        <AdjustSpaceModal
          item={adjustItem}
          onClose={() => setAdjustItem(null)}
          onSaved={() => {
            setAdjustItem(null);
            invalidate();
          }}
        />
      )}
    </div>
  );
}
